// ============================================================================
// Simplified Employee Onboarding
// Collects only essential information - tax and banking handled in Compliance
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Phone,
  MapPin,
  Heart,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Shield,
  Save,
  Loader2,
  ArrowRight,
  FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { db } from '../../lib/db';
import { useAuthStore } from '../../store/authStore';
import { getStateDropdownOptions } from '../../lib/stateTaxData';

// Simplified Onboarding Steps - Just essential info
const ONBOARDING_STEPS = [
  { id: 'welcome', title: 'Welcome', icon: CheckCircle, description: 'Get started' },
  { id: 'personal', title: 'Personal Info', icon: User, description: 'Basic details' },
  { id: 'contact', title: 'Contact', icon: MapPin, description: 'Address & phone' },
  { id: 'emergency', title: 'Emergency Contact', icon: Heart, description: 'Emergency info' },
];

// US States for dropdown
const US_STATES = getStateDropdownOptions();

export default function EmployeeOnboardingSimplified() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Simplified form data - only essential fields
  const [formData, setFormData] = useState({
    // Personal Information
    personal: {
      firstName: user?.profile?.firstName || user?.name?.split(' ')[0] || '',
      lastName: user?.profile?.lastName || user?.name?.split(' ').slice(1).join(' ') || '',
      preferredName: '',
      dateOfBirth: '',
    },
    
    // Contact Information
    contact: {
      email: user?.email || '',
      phone: user?.profile?.phone || '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
    },
    
    // Emergency Contact
    emergency: {
      name: '',
      relationship: '',
      phone: '',
    },
  });
  
  // Load existing data if any
  useEffect(() => {
    if (user?.profile) {
      setFormData(prev => ({
        ...prev,
        personal: {
          ...prev.personal,
          firstName: user.profile.firstName || prev.personal.firstName,
          lastName: user.profile.lastName || prev.personal.lastName,
          preferredName: user.profile.preferredName || '',
          dateOfBirth: user.profile.dateOfBirth || '',
        },
        contact: {
          ...prev.contact,
          email: user.email || prev.contact.email,
          phone: user.profile.phone || '',
          address1: user.profile.address?.mailing?.address1 || '',
          address2: user.profile.address?.mailing?.address2 || '',
          city: user.profile.address?.mailing?.city || '',
          state: user.profile.address?.mailing?.state || '',
          zip: user.profile.address?.mailing?.zip || '',
        },
        emergency: {
          name: user.profile.emergencyContacts?.[0]?.name || '',
          relationship: user.profile.emergencyContacts?.[0]?.relationship || '',
          phone: user.profile.emergencyContacts?.[0]?.phone || '',
        },
      }));
    }
  }, [user]);
  
  // Update form data
  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    // Clear error when field is updated
    if (errors[`${section}.${field}`]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[`${section}.${field}`];
        return updated;
      });
    }
  };
  
  // Validate current step
  const validateStep = () => {
    const newErrors = {};
    const step = ONBOARDING_STEPS[currentStep];
    
    switch (step.id) {
      case 'personal':
        if (!formData.personal.firstName?.trim()) newErrors['personal.firstName'] = 'First name is required';
        if (!formData.personal.lastName?.trim()) newErrors['personal.lastName'] = 'Last name is required';
        break;
        
      case 'contact':
        if (!formData.contact.phone?.trim()) newErrors['contact.phone'] = 'Phone number is required';
        if (!formData.contact.address1?.trim()) newErrors['contact.address1'] = 'Address is required';
        if (!formData.contact.city?.trim()) newErrors['contact.city'] = 'City is required';
        if (!formData.contact.state) newErrors['contact.state'] = 'State is required';
        if (!formData.contact.zip?.trim()) newErrors['contact.zip'] = 'ZIP code is required';
        break;
        
      case 'emergency':
        if (!formData.emergency.name?.trim()) newErrors['emergency.name'] = 'Emergency contact name is required';
        if (!formData.emergency.phone?.trim()) newErrors['emergency.phone'] = 'Emergency contact phone is required';
        if (!formData.emergency.relationship?.trim()) newErrors['emergency.relationship'] = 'Relationship is required';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle next step
  const handleNext = () => {
    if (currentStep === 0 || validateStep()) {
      if (currentStep < ONBOARDING_STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };
  
  // Handle previous step
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // Save and complete basic onboarding
  const handleComplete = async () => {
    if (!validateStep()) return;
    
    setSaving(true);
    
    try {
      const now = Date.now();
      
      // Update user profile with basic info
      const updatedProfile = {
        ...user.profile,
        firstName: formData.personal.firstName,
        lastName: formData.personal.lastName,
        preferredName: formData.personal.preferredName,
        dateOfBirth: formData.personal.dateOfBirth,
        phone: formData.contact.phone,
        address: {
          mailing: {
            address1: formData.contact.address1,
            address2: formData.contact.address2,
            city: formData.contact.city,
            state: formData.contact.state,
            zip: formData.contact.zip,
          },
        },
        emergencyContacts: [{
          name: formData.emergency.name,
          relationship: formData.emergency.relationship,
          phone: formData.emergency.phone,
          isPrimary: true,
        }],
        basicOnboardingCompleteAt: now,
      };
      
      // Update user in database
      await db.users.update(user.id, {
        name: `${formData.personal.firstName} ${formData.personal.lastName}`,
        profile: updatedProfile,
        onboardingComplete: true,
        updatedAt: now,
      });
      
      // Update auth store
      updateUser({
        name: `${formData.personal.firstName} ${formData.personal.lastName}`,
        profile: updatedProfile,
        onboardingComplete: true,
      });
      
      toast.success('Basic information saved!', {
        description: 'Now complete your compliance forms for payroll setup.'
      });
      
      // Navigate to compliance center
      const roleRoutes = {
        admin: '/admin/compliance',
        ceo: '/ceo/compliance',
        supervisor: '/supervisor/compliance',
        employee: '/employee/compliance',
      };
      navigate(roleRoutes[user.role] || '/employee/compliance');
      
    } catch (error) {
      console.error('Error saving onboarding data:', error);
      toast.error('Failed to save information', {
        description: error.message
      });
    } finally {
      setSaving(false);
    }
  };
  
  // Calculate progress
  const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;
  
  // Render Welcome Step
  const renderWelcomeStep = () => (
    <div className="text-center py-8 space-y-6">
      <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mx-auto flex items-center justify-center">
        <CheckCircle className="h-10 w-10 text-white" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Welcome to the Team, {formData.personal.firstName || 'New Team Member'}!
        </h2>
        <p className="text-gray-600 mt-2 max-w-md mx-auto">
          Let's get you set up quickly. We just need some basic information to get started.
        </p>
      </div>
      
      <Card className="max-w-lg mx-auto bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-blue-900 mb-3">Quick Setup Process:</h3>
          <div className="space-y-3 text-left">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium">1</div>
              <span className="text-sm text-blue-800">Basic personal info (name, DOB)</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium">2</div>
              <span className="text-sm text-blue-800">Contact & address</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium">3</div>
              <span className="text-sm text-blue-800">Emergency contact</span>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <FileText className="h-4 w-4" />
            <span>Tax forms & direct deposit will be completed in the Compliance Center</span>
          </div>
        </CardContent>
      </Card>
      
      <Alert className="max-w-lg mx-auto bg-amber-50 border-amber-200">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800">Compliance Required</AlertTitle>
        <AlertDescription className="text-amber-700">
          After this quick setup, you'll complete your I-9, W-4, and other required forms 
          in the Compliance Center. Your data will sync automatically!
        </AlertDescription>
      </Alert>
    </div>
  );
  
  // Render Personal Info Step
  const renderPersonalStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
        <p className="text-gray-500">Tell us a bit about yourself</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
          <Input
            id="firstName"
            value={formData.personal.firstName}
            onChange={(e) => updateFormData('personal', 'firstName', e.target.value)}
            className={errors['personal.firstName'] ? 'border-red-500' : ''}
            placeholder="John"
          />
          {errors['personal.firstName'] && (
            <p className="text-sm text-red-500">{errors['personal.firstName']}</p>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
          <Input
            id="lastName"
            value={formData.personal.lastName}
            onChange={(e) => updateFormData('personal', 'lastName', e.target.value)}
            className={errors['personal.lastName'] ? 'border-red-500' : ''}
            placeholder="Smith"
          />
          {errors['personal.lastName'] && (
            <p className="text-sm text-red-500">{errors['personal.lastName']}</p>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="preferredName">Preferred Name</Label>
          <Input
            id="preferredName"
            value={formData.personal.preferredName}
            onChange={(e) => updateFormData('personal', 'preferredName', e.target.value)}
            placeholder="Johnny (optional)"
          />
          <p className="text-xs text-gray-500">What should we call you?</p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="dateOfBirth">Date of Birth</Label>
          <Input
            id="dateOfBirth"
            type="date"
            value={formData.personal.dateOfBirth}
            onChange={(e) => updateFormData('personal', 'dateOfBirth', e.target.value)}
          />
          <p className="text-xs text-gray-500">For compliance forms (can be added later)</p>
        </div>
      </div>
      
      <Alert className="bg-gray-50 border-gray-200">
        <Shield className="h-4 w-4 text-gray-600" />
        <AlertDescription className="text-gray-600">
          Additional details like SSN will be collected securely when you complete your compliance forms (I-9, W-4).
        </AlertDescription>
      </Alert>
    </div>
  );
  
  // Render Contact Step
  const renderContactStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Contact Information</h2>
        <p className="text-gray-500">How can we reach you?</p>
      </div>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={formData.contact.email}
              disabled
              className="bg-gray-100"
            />
            <p className="text-xs text-gray-500">Your login email</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number <span className="text-red-500">*</span></Label>
            <Input
              id="phone"
              type="tel"
              value={formData.contact.phone}
              onChange={(e) => updateFormData('contact', 'phone', e.target.value)}
              className={errors['contact.phone'] ? 'border-red-500' : ''}
              placeholder="(555) 123-4567"
            />
            {errors['contact.phone'] && (
              <p className="text-sm text-red-500">{errors['contact.phone']}</p>
            )}
          </div>
        </div>
        
        <Separator />
        
        <h3 className="font-medium text-gray-900">Mailing Address</h3>
        
        <div className="space-y-2">
          <Label htmlFor="address1">Street Address <span className="text-red-500">*</span></Label>
          <Input
            id="address1"
            value={formData.contact.address1}
            onChange={(e) => updateFormData('contact', 'address1', e.target.value)}
            className={errors['contact.address1'] ? 'border-red-500' : ''}
            placeholder="123 Main Street"
          />
          {errors['contact.address1'] && (
            <p className="text-sm text-red-500">{errors['contact.address1']}</p>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="address2">Apt/Suite (optional)</Label>
          <Input
            id="address2"
            value={formData.contact.address2}
            onChange={(e) => updateFormData('contact', 'address2', e.target.value)}
            placeholder="Apt 4B"
          />
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="col-span-2 space-y-2">
            <Label htmlFor="city">City <span className="text-red-500">*</span></Label>
            <Input
              id="city"
              value={formData.contact.city}
              onChange={(e) => updateFormData('contact', 'city', e.target.value)}
              className={errors['contact.city'] ? 'border-red-500' : ''}
              placeholder="San Francisco"
            />
            {errors['contact.city'] && (
              <p className="text-sm text-red-500">{errors['contact.city']}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="state">State <span className="text-red-500">*</span></Label>
            <Select
              value={formData.contact.state}
              onValueChange={(value) => updateFormData('contact', 'state', value)}
            >
              <SelectTrigger className={errors['contact.state'] ? 'border-red-500' : ''}>
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((state) => (
                  <SelectItem key={state.value} value={state.value}>
                    {state.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors['contact.state'] && (
              <p className="text-sm text-red-500">{errors['contact.state']}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="zip">ZIP Code <span className="text-red-500">*</span></Label>
            <Input
              id="zip"
              value={formData.contact.zip}
              onChange={(e) => updateFormData('contact', 'zip', e.target.value)}
              className={errors['contact.zip'] ? 'border-red-500' : ''}
              placeholder="94102"
            />
            {errors['contact.zip'] && (
              <p className="text-sm text-red-500">{errors['contact.zip']}</p>
            )}
          </div>
        </div>
      </div>
      
      <Alert className="bg-blue-50 border-blue-200">
        <MapPin className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-700">
          This address will be pre-filled in your compliance forms. You can update it if needed when completing forms.
        </AlertDescription>
      </Alert>
    </div>
  );
  
  // Render Emergency Contact Step
  const renderEmergencyStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Emergency Contact</h2>
        <p className="text-gray-500">Who should we contact in case of emergency?</p>
      </div>
      
      <Card className="border-red-100">
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="emergencyName">Contact Name <span className="text-red-500">*</span></Label>
              <Input
                id="emergencyName"
                value={formData.emergency.name}
                onChange={(e) => updateFormData('emergency', 'name', e.target.value)}
                className={errors['emergency.name'] ? 'border-red-500' : ''}
                placeholder="Jane Smith"
              />
              {errors['emergency.name'] && (
                <p className="text-sm text-red-500">{errors['emergency.name']}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="emergencyRelationship">Relationship <span className="text-red-500">*</span></Label>
              <Select
                value={formData.emergency.relationship}
                onValueChange={(value) => updateFormData('emergency', 'relationship', value)}
              >
                <SelectTrigger className={errors['emergency.relationship'] ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spouse">Spouse</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="sibling">Sibling</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="friend">Friend</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {errors['emergency.relationship'] && (
                <p className="text-sm text-red-500">{errors['emergency.relationship']}</p>
              )}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="emergencyPhone">Phone Number <span className="text-red-500">*</span></Label>
            <Input
              id="emergencyPhone"
              type="tel"
              value={formData.emergency.phone}
              onChange={(e) => updateFormData('emergency', 'phone', e.target.value)}
              className={errors['emergency.phone'] ? 'border-red-500' : ''}
              placeholder="(555) 987-6543"
            />
            {errors['emergency.phone'] && (
              <p className="text-sm text-red-500">{errors['emergency.phone']}</p>
            )}
          </div>
        </CardContent>
      </Card>
      
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 text-center">
        <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-3" />
        <h3 className="font-semibold text-gray-900">Almost Done!</h3>
        <p className="text-sm text-gray-600 mt-1">
          Click "Complete & Continue" to save your info and proceed to compliance forms.
        </p>
      </div>
    </div>
  );
  
  // Render step content
  const renderStepContent = () => {
    const step = ONBOARDING_STEPS[currentStep];
    
    switch (step.id) {
      case 'welcome':
        return renderWelcomeStep();
      case 'personal':
        return renderPersonalStep();
      case 'contact':
        return renderContactStep();
      case 'emergency':
        return renderEmergencyStep();
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Employee Onboarding</h1>
          <p className="text-gray-600 mt-2">Quick setup to get you started</p>
        </div>
        
        {/* Progress */}
        <div className="mb-6">
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between mt-2">
            {ONBOARDING_STEPS.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center gap-1 text-xs ${
                  index <= currentStep ? 'text-blue-600' : 'text-gray-400'
                }`}
              >
                <step.icon className="h-3 w-3" />
                <span className="hidden sm:inline">{step.title}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Step Content */}
        <Card className="shadow-lg">
          <CardContent className="pt-6">
            {renderStepContent()}
          </CardContent>
        </Card>
        
        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          {currentStep < ONBOARDING_STEPS.length - 1 ? (
            <Button onClick={handleNext}>
              Continue
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleComplete}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4 mr-2" />
              )}
              Complete & Continue to Forms
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
